import { Box, FormHelperText, Grid, Typography } from '@mui/material';
import AppEventSuggest from 'components/@home/drawers/AppDrawer/Form/AppEventSuggest';
import EmbeddableButton, {
  getShareableURL,
} from 'components/@home/drawers/AppDrawer/Form/EmbeddableButton';
import EmbeddableButtonCopy from 'components/@home/drawers/AppDrawer/Form/EmbeddableButtonCopy';
import FormikAutosuggest from 'components/formik/Autosuggest';
import CopyField from 'components/formik/CopyField';
import ListField from 'components/formik/ListField';
import PhotoUpload from 'components/formik/PhotoUpload';
import Scope from 'components/formik/Scope';
import SecretField from 'components/formik/SecretField';
import Switch from 'components/formik/Switch';
import TextField from 'components/formik/TextField';
import { FastField, Field, Form } from 'formik';
import orderBy from 'lodash/orderBy';
import { bool, object } from 'prop-types';
import React, { useEffect } from 'react';
import { I18n } from 'utils/i18n';
import appShape from 'shapes/appShape';
import connector from './connector';
import formik from './formik';
import ActionButtons from './formik/ActionButtons';
import Headline from './Headline';
import Label from './Label';

const readOnlyInputSx = theme => ({
  backgroundColor: theme.palette.secondary.ultraLight,
});

const AppForm = ({ app = null, errors, isEdit, actions, loading, values, chatFlows }) => {
  const appId = app?._id;
  useEffect(() => {
    if (isEdit) {
      actions.apps.find(appId);
    }
  }, [actions.apps, appId, isEdit]);

  const handleReset = () => {
    actions.drawers.app.close();
  };

  const confirmRegenerate = ({ name }) => {
    actions.apps.regenerateSecret(appId, name);
  };

  useEffect(() => {
    actions.chatFlows.load();
  }, [actions.chatFlows]);

  const flowSuggestions = chatFlows
    ? orderBy(
        Object.values(chatFlows).map(i => ({
          value: i._id,
          label: i.name,
        })),
        [i => i.label?.toLowerCase()],
      )
    : [];

  return (
    <Box component={Form} sx={{ flexGrow: 1, padding: '20px' }}>
      <Headline isEdit={isEdit} />
      <Box
        sx={theme => ({
          display: 'flex',
          [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
          },
          paddingBottom: '30px',
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="caption" sx={{ mt: 2 }}>
              {I18n.t('AppDrawer.BASIC INFORMATION')}
            </Typography>
            <Grid container spacing={0}>
              <Grid size={2}>
                <Box sx={{ paddingTop: '20px', paddingRight: '20px' }}>
                  <FastField component={PhotoUpload} name="avatar" />
                </Box>
              </Grid>
              <Grid size={10}>
                <Grid container spacing={2}>
                  <Grid size={4}>
                    <Label>{I18n.t('AppDrawer.NameField.label')}</Label>
                    <FastField
                      component={TextField}
                      autoComplete="off"
                      placeholder={I18n.t('AppDrawer.NameField.placeholder')}
                      name="name"
                      fullWidth
                    />
                  </Grid>
                  <Grid size={8}>
                    <Label>{I18n.t('AppDrawer.DescriptionField.label')}</Label>
                    <FastField
                      component={TextField}
                      autoComplete="off"
                      placeholder={I18n.t('AppDrawer.DescriptionField.placeholder')}
                      name="description"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid spacing={2} container sx={{ alignItems: 'center' }}>
                <Grid size="auto">
                  <FastField component={Switch} name="chatbot" />
                </Grid>
                <Grid size="auto">
                  <Typography>{I18n.t('AppDrawer.Ommnio chatbot App')}</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {values.chatbot?.length ? (
            <Grid spacing={2} size={12}>
              <Typography variant="caption" sx={{ mt: 2 }}>
                {I18n.t('AppDrawer.CHATFLOW CONFIGURATION')}
              </Typography>
              <Label>{I18n.t('AppDrawer.ChatFlow.label')}</Label>
              {flowSuggestions.length ? (
                <Field
                  component={FormikAutosuggest}
                  suggestions={flowSuggestions}
                  placeholder={I18n.t('AppDrawer.ChatFlow.placeholder')}
                  name="chatFlow"
                />
              ) : (
                <Typography variant="caption" color="error">
                  {I18n.t('AppDrawer.You do not have any ChatFlows')}
                </Typography>
              )}
            </Grid>
          ) : (
            <>
              <Grid spacing={2} size={12}>
                {isEdit ? (
                  <>
                    <Typography variant="caption" sx={{ mt: 2 }}>
                      {I18n.t('AppDrawer.APP CREDENTIALS')}
                    </Typography>
                    <Grid container columnSpacing={2}>
                      <Grid size={6}>
                        <Label>Id</Label>
                        <FastField
                          component={TextField}
                          autoComplete="off"
                          name="_id"
                          slotProps={{
                            input: {
                              readOnly: true,
                              sx: readOnlyInputSx,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <Label>{I18n.t('AppDrawer.CreatedAtField.label')}</Label>
                        <FastField
                          component={TextField}
                          autoComplete="off"
                          name="createdAt"
                          slotProps={{
                            input: {
                              readOnly: true,
                              sx: readOnlyInputSx,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={6}>
                        <Label>{I18n.t('AppDrawer.baseUrl.label')}</Label>
                        <FastField component={CopyField} name="baseUrl" readOnly />
                      </Grid>
                      <Grid size={6}>
                        <Label>{I18n.t('AppDrawer.company.label')}</Label>
                        <FastField component={CopyField} name="companyId" readOnly />
                      </Grid>
                    </Grid>
                    <Label>{I18n.t('AppDrawer.SigningSecretField.label')}</Label>
                    <Box sx={{ display: 'flex' }}>
                      <FastField
                        component={SecretField}
                        name="signingSecret"
                        confirmRegenerate={confirmRegenerate}
                      />
                    </Box>
                    <Label>{I18n.t('AppDrawer.ConfigPageURLField.label')}</Label>
                    <FastField
                      component={TextField}
                      autoComplete="off"
                      name="configPageUrl"
                      placeholder="https://..."
                    />
                    <FormHelperText>
                      {I18n.t('AppDrawer.ConfigPageURLField.placeholder')}
                    </FormHelperText>
                    <Label>{I18n.t('AppDrawer.BotTokenField.label')}</Label>
                    <Box sx={{ display: 'flex' }}>
                      <FastField
                        component={SecretField}
                        name="botToken"
                        confirmRegenerate={confirmRegenerate}
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <Label>{I18n.t('AppDrawer.BotUserField.label')}</Label>
                        <Box sx={{ display: 'flex' }}>
                          <FastField component={CopyField} name="appUser" />
                        </Box>
                      </Grid>
                      <Grid size={6}>
                        <Label>{I18n.t('AppDrawer.BotEmployeeField.label')}</Label>
                        <Box sx={{ display: 'flex' }}>
                          <FastField component={CopyField} name="appEmployee" />
                        </Box>
                      </Grid>
                    </Grid>
                    <Label>{I18n.t('AppDrawer.BotScopesField.label')}</Label>
                    <FastField
                      component={ListField}
                      itemComponent={Scope}
                      name="botScopes"
                      filterOut={values.botScopes}
                    />
                  </>
                ) : null}
              </Grid>
              <Grid spacing={2} size={12}>
                <Typography variant="caption" sx={{ mt: 2 }}>
                  {I18n.t('AppDrawer.ENDPOINTS')}
                </Typography>

                <Label>{I18n.t('AppDrawer.InteractiveEndpointField.label')}</Label>
                <FastField
                  component={TextField}
                  autoComplete="off"
                  name="interactiveEndpoint"
                  placeholder="https://..."
                />
                <FormHelperText>
                  {I18n.t('AppDrawer.InteractiveEndpointField.placeholder')}
                </FormHelperText>
                <Label>{I18n.t('AppDrawer.EventEndpointField.label')}</Label>
                <FastField
                  component={TextField}
                  autoComplete="off"
                  name="eventEndpoint"
                  placeholder="https://..."
                />
                <FormHelperText>
                  {I18n.t('AppDrawer.EventEndpointField.placeholder')}
                </FormHelperText>
                <Label>{I18n.t('AppDrawer.SubscribedEventsField.label')}</Label>
                <FastField
                  component={ListField}
                  itemComponent={AppEventSuggest}
                  name="subscribedEvents"
                  filterOut={values.subscribedEvents}
                />
                <FormHelperText>
                  {I18n.t('AppDrawer.SubscribedEventsField.placeholder')}
                </FormHelperText>
              </Grid>
              <Grid spacing={2} size={12}>
                <Typography variant="caption" sx={{ mt: 2 }}>
                  {I18n.t('AppDrawer.OAuth')}
                </Typography>
                <Grid spacing={2} container sx={{ alignItems: 'center' }}>
                  <Grid size="auto">
                    <FastField component={Switch} name="enableOauth" />
                  </Grid>
                  <Grid size="auto">
                    <Typography>{I18n.t('AppDrawer.Enable OAuth')}</Typography>
                  </Grid>
                </Grid>
                {values.enableOauth?.length ? (
                  <>
                    <Label>{I18n.t('AppDrawer.AppSecretField.label')}</Label>
                    <Box sx={{ display: 'flex' }}>
                      <FastField
                        component={SecretField}
                        name="appSecret"
                        confirmRegenerate={confirmRegenerate}
                      />
                    </Box>

                    <Label>{I18n.t('AppDrawer.RedirectUrisField.label')}</Label>
                    <FastField component={ListField} name="redirectUris" />

                    <Label>{I18n.t('AppDrawer.UserScopesField.label')}</Label>
                    <FastField
                      component={ListField}
                      itemComponent={Scope}
                      name="userScopes"
                      filterOut={values.userScopes}
                    />
                    <Typography variant="caption" sx={{ mt: 4, mb: 2 }}>
                      {I18n.t('AppDrawer.SHARE YOUR APP')}
                    </Typography>
                    <Typography paragraph sx={{ color: 'secondary.main' }}>
                      {I18n.t('AppDrawer.You can use the URL')}
                    </Typography>
                    <Label>{I18n.t('AppDrawer.EmbeddableOmmnioButton')}</Label>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EmbeddableButton app={values} />
                      </Grid>
                      <Grid size={12}>
                        <EmbeddableButtonCopy app={values} />
                      </Grid>
                    </Grid>
                    <Label>{I18n.t('AppDrawer.ShareableURL')}</Label>
                    <CopyField value={getShareableURL(values)} />
                  </>
                ) : (
                  <Typography sx={{ color: 'secondary.main' }}>
                    {I18n.t('AppDrawer.You should not need OAuth')}
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {errors.server && (
            <Typography align="right" color="error" sx={{ padding: '0 20px', alignSelf: 'center' }}>
              {errors.server}
            </Typography>
          )}
        </Box>
        <ActionButtons onReset={handleReset} isEdit={isEdit} disabled={loading} />
      </Box>
    </Box>
  );
};

AppForm.propTypes = {
  app: appShape,
  actions: object.isRequired,
  isEdit: bool.isRequired,
  errors: object.isRequired,
  loading: bool.isRequired,
  values: object.isRequired,
  chatFlows: object.isRequired,
};

export default connector(formik(AppForm));
